import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography, IconButton, Box, TextField, FormControl, InputLabel,
  Select, MenuItem, Chip, Tooltip, Button, ButtonGroup,
  FormControlLabel, Checkbox,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  AREA_TYPES, AREA_TYPE_LABELS, AREA_TYPE_COLORS, AREA_CATEGORIES,
  SERVICES, SERVICE_LABELS, getAreaTypeLabel, getAreaTypeColor,
  getSprayTypeForArea, SPRAY_TYPES, SPRAY_TYPE_LABELS, SPRAY_TYPE_COLORS,
  PLANT_BEARING_TYPES, SHRUB_DENSITY_OPTIONS, MODIFIER_DEFS,
  TREE_TYPES, TREE_CONDITION_OPTIONS, COMMON_TREE_SPECIES,
  resolveAreaType,
} from '../../SiteTakeoff/siteTakeoffConstants';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 16px',
    borderBottom: '1px solid #e0e0e0',
    background: '#fafafa',
  },
  title: {
    fontSize: 15,
    fontWeight: 500,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: 16,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  geometryInfo: {
    padding: 12,
    background: '#f5f5f5',
    borderRadius: 6,
    fontSize: 12,
  },
  geometryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '2px 0',
  },
  sprayBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 10,
    borderRadius: 6,
    marginTop: 8,
  },
  serviceChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  serviceChip: {
    fontSize: 11,
    height: 24,
    cursor: 'pointer',
  },
  modifierRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 0',
    gap: 8,
  },
  modifierLabel: {
    fontSize: 12,
    color: '#555',
    flex: 1,
  },
  toggleGroup: {
    '& .MuiButton-root': {
      fontSize: 11,
      padding: '2px 8px',
      minWidth: 50,
      textTransform: 'none',
    },
  },
  densityRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  densityLabel: {
    fontSize: 12,
    color: '#555',
    minWidth: 90,
  },
}));

const formatSF = (num) => (parseFloat(num) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

/**
 * AreaEditor — focused editing view for a single area, replaces sidebar content.
 * Full V1 field parity: name, type, geometry, plant density, modifiers,
 * services, pre-emergent, tree details, notes.
 */
const AreaEditor = ({
  area,
  onClose,
  onUpdate,
  onDelete,
  isEditingShape,
  onStartEditShape,
  onStopEditShape,
}) => {
  const classes = useStyles();

  if (!area) return null;

  const resolvedType = resolveAreaType(area.areaType);
  const isPlantBearing = PLANT_BEARING_TYPES.includes(resolvedType);
  const isTreeType = TREE_TYPES.includes(resolvedType);
  const sprayType = getSprayTypeForArea(area.areaType);
  const hasPreEmergent = (area.servicesRequired || []).includes(SERVICES.PRE_EMERGENT);
  const canSpray = sprayType !== SPRAY_TYPES.NONE;

  const density = area.plantDensity || { treeCount: 0, shrubDensity: 'none', groundcoverDensity: 'none' };
  const modifiers = area.modifiers || {};
  const treeDetails = area.treeDetails || {};

  const handleTogglePreEmergent = () => {
    const current = area.servicesRequired || [];
    const next = hasPreEmergent
      ? current.filter(s => s !== SERVICES.PRE_EMERGENT)
      : [...current, SERVICES.PRE_EMERGENT];
    onUpdate({ servicesRequired: next });
  };

  const handleToggleService = (service) => {
    const current = area.servicesRequired || [];
    const next = current.includes(service)
      ? current.filter(s => s !== service)
      : [...current, service];
    onUpdate({ servicesRequired: next });
  };

  const updateDensity = (key, val) => onUpdate({ plantDensity: { [key]: val } });
  const updateModifier = (key, val) => onUpdate({ modifiers: { [key]: val } });
  const updateTreeDetail = (key, val) => onUpdate({ treeDetails: { [key]: val } });

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <IconButton size="small" onClick={onClose}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography className={classes.title}>
          {area.name || getAreaTypeLabel(area.areaType)}
        </Typography>
        {onStartEditShape && (
          <Tooltip title={isEditingShape ? 'Done editing (Esc)' : 'Edit shape'}>
            <IconButton
              size="small"
              onClick={isEditingShape ? onStopEditShape : onStartEditShape}
              style={{
                background: isEditingShape ? '#4caf50' : 'transparent',
                color: isEditingShape ? '#fff' : '#666',
              }}
            >
              {isEditingShape
                ? <CheckIcon fontSize="small" />
                : <EditIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Delete area">
          <IconButton
            size="small"
            onClick={() => {
              if (window.confirm(`Delete "${area.name || 'this area'}"?`)) onDelete();
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      {/* Banner when editing shape */}
      {isEditingShape && (
        <div style={{
          padding: '8px 16px',
          background: '#4caf50',
          color: '#fff',
          fontSize: 12,
          fontWeight: 500,
          textAlign: 'center',
        }}>
          {area.geometryType === 'point'
            ? 'Drag the marker to move it'
            : area.geometryType === 'ellipse'
              ? 'Drag the center to move or edges to resize'
              : 'Drag vertices or drag the whole shape to move'}
          <span style={{ opacity: 0.85, marginLeft: 8 }}>— Press Esc or tap ✓ when done</span>
        </div>
      )}

      <div className={classes.body}>
        {/* Name */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Name</Typography>
          <TextField
            fullWidth size="small" variant="outlined"
            value={area.name || ''}
            onChange={(e) => onUpdate({ name: e.target.value })}
            placeholder="e.g. SE Turf"
          />
        </Box>

        {/* Area Type */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Area Type</Typography>
          <FormControl fullWidth size="small" variant="outlined">
            <Select
              value={area.areaType || AREA_TYPES.GR_CLEAN}
              onChange={(e) => {
                const newType = e.target.value;
                onUpdate({
                  areaType: newType,
                  fillColor: getAreaTypeColor(newType),
                });
              }}
            >
              {Object.entries(AREA_CATEGORIES).map(([catKey, cat]) => [
                <MenuItem key={`header-${catKey}`} disabled style={{
                  opacity: 1, fontWeight: 600, fontSize: '0.72rem', background: '#f5f5f5',
                }}>
                  {cat.label}
                </MenuItem>,
                ...cat.types.map(t => (
                  <MenuItem key={t} value={t}>
                    <span style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: AREA_TYPE_COLORS[t],
                      display: 'inline-block', marginRight: 8,
                      border: '1px solid rgba(0,0,0,0.15)',
                    }} />
                    {AREA_TYPE_LABELS[t]}
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>

          {/* Pre-emergent pill */}
          <div
            className={classes.sprayBox}
            style={{
              background: canSpray ? '#fff8e1' : '#f5f5f5',
              border: `1px solid ${canSpray ? '#ffe082' : '#e0e0e0'}`,
            }}
          >
            <span style={{
              width: 10, height: 10, borderRadius: '50%',
              background: SPRAY_TYPE_COLORS[sprayType],
              border: '1px solid rgba(0,0,0,0.15)',
            }} />
            <span style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>
              Pre-Emergent: {SPRAY_TYPE_LABELS[sprayType]}
            </span>
            {canSpray && (
              <Chip
                size="small"
                label={hasPreEmergent ? 'Applied' : 'Skip'}
                onClick={handleTogglePreEmergent}
                style={{
                  background: hasPreEmergent ? '#4caf50' : '#e0e0e0',
                  color: hasPreEmergent ? '#fff' : '#666',
                  height: 22, fontSize: 11, cursor: 'pointer',
                }}
              />
            )}
          </div>
        </Box>

        {/* Geometry */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Geometry</Typography>
          <div className={classes.geometryInfo}>
            {area.geometryType === 'point' ? (
              <div className={classes.geometryRow}>
                <span>Location</span>
                <strong>
                  {area.paths?.[0]?.lat?.toFixed(5)}, {area.paths?.[0]?.lng?.toFixed(5)}
                </strong>
              </div>
            ) : area.geometryType === 'ellipse' ? (
              <>
                <div className={classes.geometryRow}>
                  <span>Radius</span>
                  <strong>{formatSF(area.radiusX || 0)} ft</strong>
                </div>
                <div className={classes.geometryRow}>
                  <span>Area</span>
                  <strong>{formatSF(area.area)} SF</strong>
                </div>
              </>
            ) : area.geometryType === 'polyline' ? (
              <>
                <div className={classes.geometryRow}>
                  <span>Length</span>
                  <strong>{formatSF(area.length)} LF</strong>
                </div>
                <div className={classes.geometryRow}>
                  <span>Width</span>
                  <strong>{area.width || 0} ft</strong>
                </div>
                <div className={classes.geometryRow}>
                  <span>Area</span>
                  <strong>{formatSF(area.area)} SF</strong>
                </div>
              </>
            ) : (
              <>
                <div className={classes.geometryRow}>
                  <span>Area</span>
                  <strong>{formatSF(area.area)} SF</strong>
                </div>
                <div className={classes.geometryRow}>
                  <span>Acres</span>
                  <strong>{parseFloat(area.areaAcres || 0).toFixed(3)}</strong>
                </div>
                <div className={classes.geometryRow}>
                  <span>Perimeter</span>
                  <strong>{formatSF(area.perimeter)} LF</strong>
                </div>
              </>
            )}
          </div>
        </Box>

        {/* Polyline width editor */}
        {area.geometryType === 'polyline' && (
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>Width (ft)</Typography>
            <TextField
              fullWidth size="small" variant="outlined" type="number"
              value={area.width || ''}
              onChange={(e) => {
                const width = parseFloat(e.target.value) || 0;
                const length = parseFloat(area.length) || 0;
                const newArea = (length * width).toFixed(2);
                onUpdate({
                  width,
                  area: newArea,
                  areaAcres: (length * width / 43560).toFixed(4),
                });
              }}
              inputProps={{ min: 0, step: 0.5 }}
            />
          </Box>
        )}

        {/* Plant density — only for plant-bearing types */}
        {isPlantBearing && (
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>Plant Density</Typography>
            <div className={classes.densityRow}>
              <span className={classes.densityLabel}>Trees</span>
              <TextField
                size="small" variant="outlined" type="number"
                value={density.treeCount || 0}
                onChange={(e) => updateDensity('treeCount', parseInt(e.target.value, 10) || 0)}
                inputProps={{ min: 0 }}
                style={{ width: 80 }}
              />
            </div>
            <div className={classes.densityRow}>
              <span className={classes.densityLabel}>Shrub density</span>
              <FormControl size="small" variant="outlined" style={{ flex: 1 }}>
                <Select
                  value={density.shrubDensity || 'none'}
                  onChange={(e) => updateDensity('shrubDensity', e.target.value)}
                >
                  {SHRUB_DENSITY_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className={classes.densityRow}>
              <span className={classes.densityLabel}>Groundcover</span>
              <FormControl size="small" variant="outlined" style={{ flex: 1 }}>
                <Select
                  value={density.groundcoverDensity || 'none'}
                  onChange={(e) => updateDensity('groundcoverDensity', e.target.value)}
                >
                  {SHRUB_DENSITY_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </Box>
        )}

        {/* Tree details — only for tree disposition types */}
        {isTreeType && (
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>Tree Details</Typography>
            <Autocomplete
              freeSolo
              options={COMMON_TREE_SPECIES}
              value={treeDetails.species || ''}
              onChange={(e, val) => updateTreeDetail('species', val || '')}
              onInputChange={(e, val) => updateTreeDetail('species', val || '')}
              renderInput={(params) => (
                <TextField {...params} size="small" variant="outlined" placeholder="Species" />
              )}
              size="small"
              style={{ marginBottom: 8 }}
            />
            <Box display="grid" gridTemplateColumns="1fr 1fr" gridGap={8} style={{ marginBottom: 8 }}>
              <TextField
                size="small" variant="outlined" type="number"
                label="Caliper (in)"
                value={treeDetails.caliper || ''}
                onChange={(e) => updateTreeDetail('caliper', parseFloat(e.target.value) || 0)}
                inputProps={{ min: 0, step: 0.5 }}
              />
              <TextField
                size="small" variant="outlined" type="number"
                label="Height (ft)"
                value={treeDetails.height || ''}
                onChange={(e) => updateTreeDetail('height', parseFloat(e.target.value) || 0)}
                inputProps={{ min: 0, step: 0.5 }}
              />
              <TextField
                size="small" variant="outlined" type="number"
                label="Spread (ft)"
                value={treeDetails.canopySpread || ''}
                onChange={(e) => updateTreeDetail('canopySpread', parseFloat(e.target.value) || 0)}
                inputProps={{ min: 0, step: 0.5 }}
              />
              <FormControl size="small" variant="outlined">
                <InputLabel>Condition</InputLabel>
                <Select
                  label="Condition"
                  value={treeDetails.condition || ''}
                  onChange={(e) => updateTreeDetail('condition', e.target.value)}
                >
                  {TREE_CONDITION_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}

        {/* Modifiers */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Modifiers</Typography>
          {Object.entries(MODIFIER_DEFS).map(([key, def]) => {
            const currentVal = modifiers[key] ?? def.default;
            if (def.type === 'toggle') {
              return (
                <div key={key} className={classes.modifierRow}>
                  <span className={classes.modifierLabel}>{def.label}</span>
                  <ButtonGroup size="small" className={classes.toggleGroup}>
                    {def.options.map(opt => (
                      <Button
                        key={opt}
                        variant={currentVal === opt ? 'contained' : 'outlined'}
                        color={currentVal === opt ? 'primary' : 'default'}
                        onClick={() => updateModifier(key, opt)}
                      >
                        {opt}
                      </Button>
                    ))}
                  </ButtonGroup>
                </div>
              );
            }
            if (def.type === 'select') {
              return (
                <div key={key} className={classes.modifierRow}>
                  <span className={classes.modifierLabel}>{def.label}</span>
                  <FormControl size="small" variant="outlined" style={{ minWidth: 110 }}>
                    <Select
                      value={currentVal}
                      onChange={(e) => updateModifier(key, e.target.value)}
                    >
                      {def.options.map(opt => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              );
            }
            if (def.type === 'checkbox') {
              return (
                <div key={key} className={classes.modifierRow}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!currentVal}
                        onChange={(e) => updateModifier(key, e.target.checked)}
                        color="primary"
                      />
                    }
                    label={<span className={classes.modifierLabel}>{def.label}</span>}
                  />
                </div>
              );
            }
            return null;
          })}
        </Box>

        {/* Services */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Services</Typography>
          <div className={classes.serviceChips}>
            {Object.entries(SERVICES).map(([key, svcValue]) => {
              const isActive = (area.servicesRequired || []).includes(svcValue);
              return (
                <Chip
                  key={svcValue}
                  label={SERVICE_LABELS[svcValue]}
                  size="small"
                  className={classes.serviceChip}
                  onClick={() => handleToggleService(svcValue)}
                  variant={isActive ? 'default' : 'outlined'}
                  style={{
                    background: isActive ? '#1976d2' : 'transparent',
                    color: isActive ? '#fff' : '#666',
                    borderColor: isActive ? '#1976d2' : '#ccc',
                  }}
                />
              );
            })}
          </div>
        </Box>

        {/* Notes */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Notes</Typography>
          <TextField
            fullWidth size="small" variant="outlined"
            multiline minRows={2}
            value={area.notes || ''}
            onChange={(e) => onUpdate({ notes: e.target.value })}
            placeholder="Optional notes..."
          />
        </Box>
      </div>
    </div>
  );
};

export default AreaEditor;
